import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../hooks/useAuth';
import { usePair } from '../hooks/usePair';
import { usePrayerRequests } from '../hooks/usePrayerRequests';

const {
  FiHeart, FiPlus, FiCheck, FiTrash2, FiAlertCircle,
  FiLoader, FiX, FiCheckCircle, FiClock, FiUser,
} = FiIcons;

function AddPrayerForm({ onSubmit, onCancel, loading }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    onSubmit(title.trim(), description.trim());
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-6 transition-colors"
    >
      <h3 className="text-lg font-space font-semibold text-gray-900 dark:text-white mb-4">New Prayer Request</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What would you like prayer for?"
            className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 bg-transparent dark:bg-gray-900 dark:text-white rounded-lg custom-focus text-sm transition-colors"
            required
            autoFocus
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Details <span className="text-gray-400 dark:text-gray-500 font-normal">(optional)</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="Share more about this prayer request…"
            className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 bg-transparent dark:bg-gray-900 dark:text-white rounded-lg custom-focus text-sm resize-none transition-colors"
          />
        </div>
        <div className="flex gap-3">
          <button
            type="submit"
            disabled={loading || !title.trim()}
            className="bg-primary text-white px-5 py-2.5 rounded-lg hover:bg-primary-dark transition-colors flex items-center gap-2 text-sm disabled:opacity-60"
          >
            {loading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <SafeIcon icon={FiPlus} className="w-4 h-4" />}
            {loading ? 'Adding…' : 'Add Request'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-5 py-2.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm flex items-center gap-2"
          >
            <SafeIcon icon={FiX} className="w-4 h-4" />
            Cancel
          </button>
        </div>
      </form>
    </motion.div>
  );
}

function PrayerCard({ prayer, userId, onToggleAnswered, onDelete }) {
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isOwner = prayer.submitted_by === userId;
  const isAnswered = prayer.is_answered;

  const handleToggle = async () => {
    setToggling(true);
    try {
      await onToggleAnswered(prayer.id, !isAnswered);
    } finally {
      setToggling(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(prayer.id);
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const submitterName = prayer.submitted_by_profile?.name || 'Someone';
  const submitterRole = prayer.submitted_by_profile?.role || 'member';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`rounded-xl shadow-sm border p-5 transition-all ${
        isAnswered 
          ? 'border-green-200 dark:border-green-800/50 bg-green-50 dark:bg-green-900/10' 
          : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700'
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={`mt-0.5 w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
            isAnswered ? 'bg-green-100 dark:bg-green-900/40' : 'bg-purple-100 dark:bg-purple-900/30'
          }`}>
            <SafeIcon
              icon={isAnswered ? FiCheckCircle : FiHeart}
              className={`w-4 h-4 ${isAnswered ? 'text-green-600 dark:text-green-400' : 'text-purple-600 dark:text-purple-400'}`}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className={`font-medium ${isAnswered ? 'line-through text-gray-500 dark:text-gray-500' : 'text-gray-900 dark:text-white'}`}>
                {prayer.title}
              </h3>
              {isAnswered && (
                <span className="text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full font-medium">
                  Answered 🙏
                </span>
              )}
            </div>
            {prayer.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">{prayer.description}</p>
            )}
            <div className="flex items-center gap-3 mt-2 text-xs text-gray-400 dark:text-gray-500">
              <span className="flex items-center gap-1">
                <SafeIcon icon={FiUser} className="w-3 h-3" />
                {submitterName} ({submitterRole})
              </span>
              <span className="flex items-center gap-1">
                <SafeIcon icon={FiClock} className="w-3 h-3" />
                {new Date(prayer.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={handleToggle}
            disabled={toggling}
            title={isAnswered ? 'Mark as unanswered' : 'Mark as answered'}
            className={`p-2 rounded-lg transition-colors ${
              isAnswered
                ? 'text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30'
                : 'text-gray-400 dark:text-gray-500 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
            }`}
          >
            {toggling ? <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin block" /> : <SafeIcon icon={FiCheck} className="w-4 h-4" />}
          </button>

          {isOwner && (
            confirmDelete ? (
              <div className="flex items-center gap-1">
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="text-xs bg-red-500 text-white px-2.5 py-1.5 rounded-lg hover:bg-red-600 transition-colors flex items-center gap-1 disabled:opacity-60"
                >
                  {deleting && <span className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />}
                  Delete
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-2 py-1.5"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDelete(true)}
                className="p-2 rounded-lg text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                title="Delete prayer request"
              >
                <SafeIcon icon={FiTrash2} className="w-4 h-4" />
              </button>
            )
          )}
        </div>
      </div>
    </motion.div>
  );
}

function PrayerRequests({ userRole }) {
  const { user } = useAuth();
  const { pair, loading: pairLoading } = usePair(user?.id, userRole);
  const { prayers, loading, error, addPrayer, toggleAnswered, removePrayer } = usePrayerRequests(pair?.id, user?.id);

  const [showForm, setShowForm] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');

  const handleAdd = async (title, description) => {
    setAdding(true);
    setAddError('');
    try {
      await addPrayer(title, description);
      setShowForm(false);
    } catch (err) {
      setAddError(err.message || 'Failed to add prayer request.');
    } finally {
      setAdding(false);
    }
  };

  const active = prayers.filter((p) => !p.is_answered);
  const answered = prayers.filter((p) => p.is_answered);
  const noPair = !pairLoading && !pair;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 transition-colors duration-200">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-space font-bold text-gray-900 dark:text-white mb-1">Prayer Requests</h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              {noPair ? 'Link a pair to share prayer requests.' : `${active.length} active · ${answered.length} answered`}
            </p>
          </div>
          {pair && !showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="bg-primary text-white px-4 py-2.5 rounded-lg hover:bg-primary-dark transition-colors flex items-center gap-2 text-sm font-medium"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
              Add Request
            </button>
          )}
        </div>
      </motion.div>

      {/* No pair warning */}
      {noPair && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/50 rounded-xl p-4 mb-6 flex items-start gap-3 transition-colors">
          <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-yellow-800 dark:text-yellow-300">
            {userRole === 'leader'
              ? 'Link a disciple from your Profile page to start sharing prayer requests.'
              : "You're not linked to a discipleship pair yet. Ask your leader to add you."}
          </p>
        </div>
      )}

      {addError && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/50 rounded-lg text-sm text-red-600 dark:text-red-400 flex items-center gap-2 transition-colors">
          <SafeIcon icon={FiAlertCircle} className="w-4 h-4 flex-shrink-0" />
          {addError}
        </div>
      )}

      {/* Add form */}
      <AnimatePresence>
        {showForm && (
          <AddPrayerForm onSubmit={handleAdd} onCancel={() => setShowForm(false)} loading={adding} />
        )}
      </AnimatePresence>

      {/* Loading */}
      {(loading || pairLoading) && (
        <div className="text-center py-16">
          <SafeIcon icon={FiLoader} className="w-8 h-8 text-primary mx-auto animate-spin mb-3" />
          <p className="text-gray-500 dark:text-gray-400 text-sm">Loading prayer requests…</p>
        </div>
      )}

      {error && !loading && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/50 rounded-xl text-sm text-red-600 dark:text-red-400 flex items-center gap-2 transition-colors">
          <SafeIcon icon={FiAlertCircle} className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Empty state */}
      {!loading && !pairLoading && pair && prayers.length === 0 && !showForm && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-12 text-center transition-colors">
          <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiHeart} className="w-8 h-8 text-purple-500 dark:text-purple-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No prayer requests yet</h3>
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">Share what's on your heart with your discipleship partner.</p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-primary text-white px-5 py-2.5 rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
          >
            Add First Request
          </button>
        </motion.div>
      )}

      {/* Active requests */}
      {!loading && !pairLoading && active.length > 0 && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
            Active ({active.length})
          </h2>
          <div className="space-y-3">
            <AnimatePresence>
              {active.map((p) => (
                <PrayerCard
                  key={p.id}
                  prayer={p}
                  userId={user?.id}
                  onToggleAnswered={toggleAnswered}
                  onDelete={removePrayer}
                />
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Answered requests */}
      {!loading && !pairLoading && answered.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
            Answered 🙏 ({answered.length})
          </h2>
          <div className="space-y-3">
            <AnimatePresence>
              {answered.map((p) => (
                <PrayerCard
                  key={p.id}
                  prayer={p}
                  userId={user?.id}
                  onToggleAnswered={toggleAnswered}
                  onDelete={removePrayer}
                />
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}
    </div>
  );
}

export default PrayerRequests;